import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { 
  Mail,
  Save,
  RotateCcw,
  TestTube,
  AlertTriangle,
  Loader,
  Send,
  Download,
  Eye,
  EyeOff,
  Settings,
  Globe,
  FileText
} from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTheme } from '../../../../contexts/ThemeContext';
import { api } from '../../../../utils/api';

const GlobalEmailConfig = () => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  
  // Check if user is super admin
  const isSuperAdmin = user?.userType === 'admin' && (user?.isSuperAdmin === true || user?.role === 'SuperAdmin');
  
  console.log('🔍 GlobalEmailConfig - User:', user);
  console.log('🔍 GlobalEmailConfig - isSuperAdmin:', isSuperAdmin);
  
  const [globalConfig, setGlobalConfig] = useState({
    global_email_settings: {
      system_smtp: {
        enabled: false,
        host: '',
        port: 587,
        secure: false,
        username: '',
        password: '',
        fromName: 'CRM System',
        fromAddress: 'noreply@company.com',
        showPassword: false
      },
      system_imap: {
        enabled: false,
        host: '',
        port: 993,
        secure: true,
        username: '',
        password: '',
        showPassword: false
      },
      global_processing: {
        enabled: false,
        checkInterval: 300000,
        maxEmailsPerCheck: 50,
        deleteAfterProcessing: false,
        systemNotifications: true,
        adminAlerts: true,
        systemEmailPatterns: ['system@', 'admin@', 'noreply@', 'support@']
      },
      email_templates: {
        welcome_email: {
          subject: 'Welcome to CRM System',
          template: 'welcome_template.html'
        },
        password_reset: {
          subject: 'Password Reset Request',
          template: 'password_reset_template.html'
        },
        tenant_created: {
          subject: 'New Tenant Created',
          template: 'tenant_created_template.html'
        }
      }
    }
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({ smtp: false, imap: false });

  useEffect(() => {
    if (isSuperAdmin) {
      loadGlobalEmailConfig();
    }
  }, [isSuperAdmin]);

  const loadGlobalEmailConfig = async () => {
    try {
      setLoading(true);
      console.log('🔍 Loading global email configuration');
      console.log('🔍 User info:', user);
      console.log('🔍 Is super admin:', isSuperAdmin);
      
      const configData = await api.get('/global-email-config');
      
      console.log('✅ Received global email config:', configData);
      console.log('✅ Config structure:', JSON.stringify(configData, null, 2));
      
      if (configData && configData.global_email_settings) {
        console.log('✅ Setting global config:', configData);
        setGlobalConfig(configData);
      } else {
        console.warn('⚠️ No valid global email configuration found, using defaults');
        console.warn('⚠️ Received data:', configData);
      }
    } catch (error) {
      console.error('❌ Failed to load global email configuration:', error);
      console.error('❌ Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        url: error.config?.url
      });
      toast.error('Failed to load global email configuration: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const saveGlobalEmailConfig = async () => {
    try {
      setSaving(true);
      console.log('💾 Saving global email configuration:', globalConfig);
      console.log('💾 Current user:', user);
      console.log('💾 Is super admin check:', isSuperAdmin);
      console.log('💾 Token from localStorage:', localStorage.getItem('token')?.substring(0, 50) + '...');
      
      const response = await api.put('/global-email-config', globalConfig);
      console.log('✅ Global email config saved:', response);
      toast.success('Global email configuration saved successfully');
    } catch (error) {
      console.error('❌ Failed to save global email configuration:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Error data:', error.response?.data);
      toast.error('Failed to save global email configuration: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  const updateSmtpConfig = (field, value) => {
    if (field === 'host' && typeof value === 'string') {
      value = value.trim();
    }
    
    setGlobalConfig(prev => ({
      ...prev,
      global_email_settings: {
        ...prev.global_email_settings,
        system_smtp: {
          ...prev.global_email_settings.system_smtp,
          [field]: value
        }
      }
    }));
  };

  const updateImapConfig = (field, value) => {
    if (field === 'host' && typeof value === 'string') {
      value = value.trim();
    }
    
    setGlobalConfig(prev => ({
      ...prev,
      global_email_settings: {
        ...prev.global_email_settings,
        system_imap: {
          ...prev.global_email_settings.system_imap,
          [field]: value
        }
      }
    }));
  };

  const updateProcessingConfig = (field, value) => {
    setGlobalConfig(prev => ({
      ...prev,
      global_email_settings: {
        ...prev.global_email_settings,
        global_processing: {
          ...prev.global_email_settings.global_processing,
          [field]: value
        }
      }
    }));
  };

  const updateEmailPattern = (index, value) => {
    setGlobalConfig(prev => ({
      ...prev,
      global_email_settings: {
        ...prev.global_email_settings,
        global_processing: {
          ...prev.global_email_settings.global_processing,
          systemEmailPatterns: prev.global_email_settings.global_processing.systemEmailPatterns.map((pattern, i) => 
            i === index ? value : pattern
          )
        }
      }
    }));
  };

  const addEmailPattern = () => {
    setGlobalConfig(prev => ({
      ...prev,
      global_email_settings: {
        ...prev.global_email_settings,
        global_processing: {
          ...prev.global_email_settings.global_processing,
          systemEmailPatterns: [...prev.global_email_settings.global_processing.systemEmailPatterns, '']
        }
      }
    }));
  };

  const removeEmailPattern = (index) => {
    setGlobalConfig(prev => ({
      ...prev,
      global_email_settings: {
        ...prev.global_email_settings,
        global_processing: {
          ...prev.global_email_settings.global_processing,
          systemEmailPatterns: prev.global_email_settings.global_processing.systemEmailPatterns.filter((_, i) => i !== index)
        }
      }
    }));
  };

  const testSmtpConnection = async () => {
    try {
      setTesting(prev => ({ ...prev, smtp: true }));
      await api.post('/global-email-config/test-smtp', globalConfig.global_email_settings.system_smtp);
      toast.success('Global SMTP connection test successful');
    } catch (error) {
      console.error('Global SMTP test failed:', error);
      toast.error('Global SMTP connection test failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setTesting(prev => ({ ...prev, smtp: false }));
    }
  };

  const testImapConnection = async () => {
    try {
      setTesting(prev => ({ ...prev, imap: true }));
      await api.post('/global-email-config/test-imap', globalConfig.global_email_settings.system_imap);
      toast.success('Global IMAP connection test successful');
    } catch (error) {
      console.error('Global IMAP test failed:', error);
      toast.error('Global IMAP connection test failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setTesting(prev => ({ ...prev, imap: false }));
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader className="mx-auto h-8 w-8 animate-spin text-blue-600" />
          <p className="mt-2 text-sm text-gray-600">Loading global email configuration...</p>
        </div>
      </div>
    );
  }

  // Show access denied if user is not super admin
  if (!isSuperAdmin) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-yellow-400" />
          <h3 className={`mt-2 text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Super Admin Access Required</h3>
          <p className={`mt-1 text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
            Only super administrators can access global email configuration.
          </p>
        </div>
      </div>
    );
  }

  const { system_smtp, system_imap, global_processing } = globalConfig.global_email_settings;

  return (
    <div className={`space-y-6 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold flex items-center space-x-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Globe className="h-7 w-7 text-blue-600" />
            <span>Global Email Configuration</span>
          </h1>
          <p className={`mt-1 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            System-wide email settings for CRM operations
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => window.location.reload()}
            className={`flex items-center space-x-2 px-4 py-2 border rounded-md ${
              isDark 
                ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' 
                : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
            }`}
          >
            <RotateCcw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
          <button
            onClick={saveGlobalEmailConfig}
            disabled={saving}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? (
              <Loader className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>{saving ? 'Saving...' : 'Save Configuration'}</span>
          </button>
        </div>
      </div>

      {/* System SMTP Configuration */}
      <div className={`rounded-lg shadow ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-blue-100'}`}>
                <Send className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h2 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>System SMTP Configuration</h2>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Global outgoing email settings</p>
              </div>
            </div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={system_smtp.enabled}
                onChange={(e) => updateSmtpConfig('enabled', e.target.checked)}
                className={`rounded text-blue-600 focus:ring-blue-500 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              />
              <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Enabled</span>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                SMTP Host
              </label>
              <input
                type="text"
                value={system_smtp.host}
                onChange={(e) => updateSmtpConfig('host', e.target.value)}
                disabled={!system_smtp.enabled}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  isDark 
                    ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                    : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                }`}
                placeholder="smtp.gmail.com"
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Port
              </label>
              <input
                type="number"
                value={system_smtp.port}
                onChange={(e) => updateSmtpConfig('port', parseInt(e.target.value))}
                disabled={!system_smtp.enabled}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  isDark 
                    ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                    : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                }`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Username
              </label>
              <input
                type="text"
                value={system_smtp.username}
                onChange={(e) => updateSmtpConfig('username', e.target.value)}
                disabled={!system_smtp.enabled}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  isDark 
                    ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                    : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                }`}
                placeholder="system-email@company.com"
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Password
              </label>
              <div className="relative">
                <input
                  type={system_smtp.showPassword ? 'text' : 'password'}
                  value={system_smtp.password}
                  onChange={(e) => updateSmtpConfig('password', e.target.value)}
                  disabled={!system_smtp.enabled}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10 ${
                    isDark 
                      ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                      : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                  }`}
                />
                <span className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  {system_smtp.showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400 cursor-pointer" onClick={() => updateSmtpConfig('showPassword', false)} />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400 cursor-pointer" onClick={() => updateSmtpConfig('showPassword', true)} />
                  )}
                </span>
              </div>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                From Name
              </label>
              <input
                type="text"
                value={system_smtp.fromName}
                onChange={(e) => updateSmtpConfig('fromName', e.target.value)}
                disabled={!system_smtp.enabled}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  isDark 
                    ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                    : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                }`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                From Address
              </label>
              <input
                type="email"
                value={system_smtp.fromAddress}
                onChange={(e) => updateSmtpConfig('fromAddress', e.target.value)}
                disabled={!system_smtp.enabled}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  isDark 
                    ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                    : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                }`}
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={system_smtp.secure}
                onChange={(e) => updateSmtpConfig('secure', e.target.checked)}
                disabled={!system_smtp.enabled}
                className={`mr-2 rounded text-blue-600 focus:ring-blue-500 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              />
              <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Use SSL/TLS</label>
            </div>
            <div className="flex items-end">
              <button
                onClick={testSmtpConnection}
                disabled={testing.smtp || !system_smtp.enabled || !system_smtp.host || !system_smtp.username}
                className={`w-full flex items-center justify-center space-x-2 px-4 py-2 border rounded-md disabled:opacity-50 ${
                  isDark 
                    ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' 
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                }`}
              >
                {testing.smtp ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  <TestTube className="h-4 w-4" />
                )}
                <span>{testing.smtp ? 'Testing...' : 'Test Connection'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* System IMAP Configuration */}
      <div className={`rounded-lg shadow ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-green-100'}`}>
                <Download className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h2 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>System IMAP Configuration</h2>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Global incoming email settings</p>
              </div>
            </div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={system_imap.enabled}
                onChange={(e) => updateImapConfig('enabled', e.target.checked)}
                className={`rounded text-green-600 focus:ring-green-500 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              />
              <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Enabled</span>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                IMAP Host
              </label>
              <input
                type="text"
                value={system_imap.host}
                onChange={(e) => updateImapConfig('host', e.target.value)}
                disabled={!system_imap.enabled}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                  isDark 
                    ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                    : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                }`}
                placeholder="imap.gmail.com"
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Port
              </label>
              <input
                type="number"
                value={system_imap.port}
                onChange={(e) => updateImapConfig('port', parseInt(e.target.value))}
                disabled={!system_imap.enabled}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                  isDark 
                    ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                    : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                }`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Username
              </label>
              <input
                type="text"
                value={system_imap.username}
                onChange={(e) => updateImapConfig('username', e.target.value)}
                disabled={!system_imap.enabled}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                  isDark 
                    ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                    : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                }`}
                placeholder="system-email@company.com"
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Password
              </label>
              <div className="relative">
                <input
                  type={system_imap.showPassword ? 'text' : 'password'}
                  value={system_imap.password}
                  onChange={(e) => updateImapConfig('password', e.target.value)}
                  disabled={!system_imap.enabled}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500 pr-10 ${
                    isDark 
                      ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                      : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                  }`}
                />
                <span className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  {system_imap.showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400 cursor-pointer" onClick={() => updateImapConfig('showPassword', false)} />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400 cursor-pointer" onClick={() => updateImapConfig('showPassword', true)} />
                  )}
                </span>
              </div>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={system_imap.secure}
                onChange={(e) => updateImapConfig('secure', e.target.checked)}
                disabled={!system_imap.enabled}
                className={`mr-2 rounded text-green-600 focus:ring-green-500 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              />
              <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Use SSL/TLS</label>
            </div>
            <div className="flex items-end">
              <button
                onClick={testImapConnection}
                disabled={testing.imap || !system_imap.enabled || !system_imap.host || !system_imap.username}
                className={`w-full flex items-center justify-center space-x-2 px-4 py-2 border rounded-md disabled:opacity-50 ${
                  isDark 
                    ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' 
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                }`}
              >
                {testing.imap ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  <TestTube className="h-4 w-4" />
                )}
                <span>{testing.imap ? 'Testing...' : 'Test Connection'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Global Processing Settings */}
      <div className={`rounded-lg shadow ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-purple-100'}`}>
                <Settings className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h2 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Global Processing Settings</h2>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>System-wide email processing configuration</p>
              </div>
            </div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={global_processing.enabled}
                onChange={(e) => updateProcessingConfig('enabled', e.target.checked)}
                className={`rounded text-purple-600 focus:ring-purple-500 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              />
              <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Enabled</span>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Check Interval (minutes)
              </label>
              <input
                type="number"
                value={Math.floor(global_processing.checkInterval / 60000)}
                onChange={(e) => updateProcessingConfig('checkInterval', parseInt(e.target.value) * 60000)}
                disabled={!global_processing.enabled}
                min="1"
                max="60"
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                  isDark 
                    ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                    : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                }`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Max Emails Per Check
              </label>
              <input
                type="number"
                value={global_processing.maxEmailsPerCheck}
                onChange={(e) => updateProcessingConfig('maxEmailsPerCheck', parseInt(e.target.value))}
                disabled={!global_processing.enabled}
                min="1"
                max="100"
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                  isDark 
                    ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                    : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                }`}
              />
            </div>
          </div>

          <div className="space-y-4 mb-6">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={global_processing.systemNotifications}
                onChange={(e) => updateProcessingConfig('systemNotifications', e.target.checked)}
                disabled={!global_processing.enabled}
                className={`mr-3 rounded text-purple-600 focus:ring-purple-500 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              />
              <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Enable system notifications
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={global_processing.adminAlerts}
                onChange={(e) => updateProcessingConfig('adminAlerts', e.target.checked)}
                disabled={!global_processing.enabled}
                className={`mr-3 rounded text-purple-600 focus:ring-purple-500 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              />
              <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Enable admin alerts
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={global_processing.deleteAfterProcessing}
                onChange={(e) => updateProcessingConfig('deleteAfterProcessing', e.target.checked)}
                disabled={!global_processing.enabled}
                className={`mr-3 rounded text-purple-600 focus:ring-purple-500 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              />
              <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Delete emails after processing
              </label>
            </div>
          </div>

          {/* System Email Patterns */}
          <div>
            <h4 className={`text-md font-medium mb-3 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
              System Email Patterns
            </h4>
            <div className="space-y-2">
              {global_processing.systemEmailPatterns.map((pattern, index) => (
                <div key={index} className="flex space-x-2">
                  <input
                    type="text"
                    value={pattern}
                    onChange={(e) => updateEmailPattern(index, e.target.value)}
                    disabled={!global_processing.enabled}
                    className={`flex-1 px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                      isDark 
                        ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                        : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                    }`}
                    placeholder="system@"
                  />
                  <button
                    onClick={() => removeEmailPattern(index)}
                    disabled={!global_processing.enabled}
                    className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                onClick={addEmailPattern}
                disabled={!global_processing.enabled}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              >
                Add Pattern
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalEmailConfig;
